import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Wallet, TrendUp, TrendDown, ChartPie, ArrowUpRight, Handshake, CurrencyDollar } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function PortfolioPage() {
  const { api, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPortfolio = useCallback(async () => {
    try {
      const { data } = await api.get('/portfolio');
      setPortfolio(data);
    } catch (err) {
      toast.error('Failed to load portfolio');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchPortfolio(); }, [fetchPortfolio]);

  const handleSell = async (tradeId) => {
    try {
      const { data } = await api.post(`/trades/${tradeId}/sell`);
      toast.success(`Position sold! Profit: $${data.profit.toFixed(2)}`);
      await refreshUser();
      fetchPortfolio();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sell failed');
    }
  };

  if (loading) return (
    <div className="min-h-screen pt-24 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-oh-green border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const pl = portfolio?.total_profit_loss || 0;
  const plColor = pl >= 0 ? 'text-[#00FFA3]' : 'text-[#FF3B30]';
  const PlIcon = pl >= 0 ? TrendUp : TrendDown;

  return (
    <div className="min-h-screen pt-24 px-4 md:px-8 pb-12" data-testid="portfolio-page">
      <div className="max-w-5xl mx-auto">
        <h1 className="font-heading text-3xl sm:text-4xl text-[var(--oh-text)] tracking-tight mb-8 animate-fade-up">Portfolio</h1>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-[var(--oh-card)] border border-[var(--oh-border)] rounded-xl p-5 animate-fade-up">
            <div className="flex items-center gap-2 mb-2">
              <Wallet size={18} className="text-[#00FFA3]" weight="fill" />
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--oh-text-secondary)]">Wallet Balance</span>
            </div>
            <p className="font-mono text-2xl font-bold text-[var(--oh-text)]" data-testid="portfolio-wallet-balance">
              ${(user?.wallet_balance || portfolio?.wallet_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>

          <div className="bg-[var(--oh-card)] border border-[var(--oh-border)] rounded-xl p-5 animate-fade-up" style={{ animationDelay: '0.05s' }}>
            <div className="flex items-center gap-2 mb-2">
              <CurrencyDollar size={18} className="text-[#00B8FF]" weight="fill" />
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--oh-text-secondary)]">Invested</span>
            </div>
            <p className="font-mono text-2xl font-bold text-[var(--oh-text)]" data-testid="portfolio-invested">
              ${(portfolio?.total_invested || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>

          <div className="bg-[var(--oh-card)] border border-[var(--oh-border)] rounded-xl p-5 animate-fade-up" style={{ animationDelay: '0.1s' }}>
            <div className="flex items-center gap-2 mb-2">
              <ChartPie size={18} className="text-[#F59E0B]" weight="fill" />
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--oh-text-secondary)]">Current Value</span>
            </div>
            <p className="font-mono text-2xl font-bold text-[var(--oh-text)]" data-testid="portfolio-current-value">
              ${(portfolio?.total_current_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>

          <div className="bg-[var(--oh-card)] border border-[var(--oh-border)] rounded-xl p-5 animate-fade-up" style={{ animationDelay: '0.15s' }}>
            <div className="flex items-center gap-2 mb-2">
              <PlIcon size={18} className={plColor} weight="fill" />
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--oh-text-secondary)]">P&L</span>
            </div>
            <p className={`font-mono text-2xl font-bold ${plColor}`} data-testid="portfolio-pnl">
              {pl >= 0 ? '+' : ''}${pl.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
        </div>

        {/* Positions */}
        <div className="animate-fade-up" style={{ animationDelay: '0.2s' }}>
          <h2 className="font-heading text-xl text-[var(--oh-text)] mb-4">Active Positions <span className="text-[var(--oh-text-muted)] font-mono text-base">({portfolio?.position_count || 0})</span></h2>
          
          {(!portfolio?.positions || portfolio.positions.length === 0) ? (
            <div className="bg-[var(--oh-card)] border border-[var(--oh-border)] rounded-xl p-12 text-center">
              <Handshake size={48} className="mx-auto mb-4 text-[var(--oh-text-muted)]" />
              <p className="text-[var(--oh-text-secondary)] text-lg mb-2">No active positions</p>
              <p className="text-[var(--oh-text-muted)] text-sm mb-6">Start trading on prediction markets to build your portfolio</p>
              <Button onClick={() => navigate('/markets')}
                className="bg-gradient-to-r from-[#00FFA3] to-[#00B8FF] text-black font-bold hover:scale-[1.02] transition-transform">
                <TrendUp size={16} className="mr-2" /> Explore Markets
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {portfolio.positions.map((pos) => {
                const posColor = pos.position === 'yes' ? '#00FFA3' : '#00B8FF';
                const plPos = pos.profit_loss || 0;
                const plPosColor = plPos >= 0 ? 'text-[#00FFA3]' : 'text-[#FF3B30]';
                return (
                  <div key={pos.trade_id} className="bg-[var(--oh-card)] border border-[var(--oh-border)] rounded-xl p-5 hover:border-[var(--oh-border-hover)] transition-colors" data-testid={`position-${pos.trade_id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm" style={{ color: posColor, backgroundColor: `${posColor}15`, border: `1px solid ${posColor}33` }}>
                            {pos.position}
                          </span>
                          <span className="text-xs text-[var(--oh-text-muted)] font-mono">{pos.shares} shares @ ${pos.buy_price.toFixed(2)}</span>
                        </div>
                        <h4 className="text-sm text-[var(--oh-text)] truncate cursor-pointer hover:text-[#00FFA3] transition-colors" onClick={() => navigate(`/market/${pos.market_id}`)}>
                          {pos.question}
                        </h4>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-xs text-[var(--oh-text-muted)]">Value</p>
                          <p className="font-mono text-sm text-[var(--oh-text)]">${pos.current_value.toFixed(2)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-[var(--oh-text-muted)]">P&L</p>
                          <p className={`font-mono text-sm ${plPosColor}`}>{plPos >= 0 ? '+' : ''}${plPos.toFixed(2)}</p>
                        </div>
                        <Button onClick={() => handleSell(pos.trade_id)} variant="outline" size="sm"
                          className="bg-[var(--oh-bg)] border-[var(--oh-border)] text-[var(--oh-text)] hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors text-xs"
                          data-testid={`sell-${pos.trade_id}`}>
                          Sell
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
